import {
  DefaultSchoolYear,
  FilterKeys,
  GetPayableFeesArrangeBy,
  GetPayableFeesQueryFilters,
  InvoicesReportingDayPastDue,
  PAYABLE_FEES_ARRANGE_BY_FILTER_KEYS,
  PayableFeeStatus,
  PaymentFrequencyType,
  SchoolYear,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  ArrangeBySelect,
  DayPastDueSelectMultiple,
  FeeStatusSelectMultiple,
  FiltersSidebar,
  FiltersSidebarMethods,
  FrequencySelectMultiple,
  ProductSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePayableFeesFilters } from './usePayableFeesFilters';

type PayableFeesFiltersSidebarProps = {
  arrangeBy: GetPayableFeesArrangeBy;
  defaultSchoolYear?: DefaultSchoolYear;
  filters: GetPayableFeesQueryFilters;
  defaultFilters: GetPayableFeesQueryFilters;
  schoolId: string;
  schoolYears: SchoolYear[];
  onApply: ({
    filters,
    arrangeBy,
  }: {
    filters: GetPayableFeesQueryFilters;
    arrangeBy: GetPayableFeesArrangeBy;
  }) => void;
};

export const PayableFeesFiltersSidebar = forwardRef<
  FiltersSidebarMethods,
  PayableFeesFiltersSidebarProps
>(
  (
    {
      filters: propsFilters,
      arrangeBy: propsArrangeBy,
      defaultFilters,
      onApply,
      schoolId,
      defaultSchoolYear,
      schoolYears,
    },
    ref,
  ) => {
    const [filters, setFilters] = useState(propsFilters);
    const [arrangeBy, setArrangeBy] = useState(propsArrangeBy);
    const { permissions } = useAuth();
    const { formatMessage } = useIntl();
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);
    const {
      labels,
      schoolYear,
      onSetDate,
      onSelectProduct,
      onSelectProductType,
      onClearProduct,
      onSetFrequency,
      onClearFrequency,
      onSetFeeStatus,
      onClearFeeStatus,
      onSetDayPastDue,
      onClearDayPastDue,
      onSetAgeGroup,
      onClearAgeGroup,
    } = usePayableFeesFilters({ schoolId, filters, onSetFilters: setFilters, schoolYears });

    const handleApply = useCallback(() => {
      onApply({ filters, arrangeBy });
    }, [filters, arrangeBy, onApply]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      setArrangeBy(propsArrangeBy);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters, propsArrangeBy]);

    const onSelectFrequency = useCallback(
      (frequency: PaymentFrequencyType) => {
        onSetFrequency(toggleMultipleValueArrayProperty(filters.frequency_types, [frequency]));
      },
      [filters.frequency_types, onSetFrequency],
    );

    const onSelectFeeStatus = useCallback(
      (status: PayableFeeStatus) => {
        onSetFeeStatus(toggleMultipleValueArrayProperty(filters.fee_status, [status]));
      },
      [filters.fee_status, onSetFeeStatus],
    );

    const onSelectDayPastDue = useCallback(
      (dayPastDue: InvoicesReportingDayPastDue) => {
        onSetDayPastDue(toggleMultipleValueArrayProperty(filters.day_past_due, [dayPastDue]));
      },
      [filters.day_past_due, onSetDayPastDue],
    );

    const onSelectAgeGroup = useCallback(
      (ageGroupId: string[]) => {
        onSetAgeGroup(toggleMultipleValueArrayProperty(filters.age_group_ids, ageGroupId));
      },
      [filters.age_group_ids, onSetAgeGroup],
    );

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
          setArrangeBy(propsArrangeBy);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-payableFees' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SchoolPeriodsRangeSelect
          ref={periodsRef}
          showSelectedPeriodLabel={false}
          schoolId={schoolId}
          defaultSchoolYear={defaultSchoolYear}
          hasManagePermission={permissions.includes('school_admin')}
          onSetDate={onSetDate}
          placeholder={labels[FilterKeys.Date]}
          date={filters.date || []}
          requiredLabel="required"
          withinSingleSchoolYear
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ProductSelectMultiple
          placeholder={labels[FilterKeys.Product]}
          schoolId={schoolId}
          selectedProductIds={filters.product_ids || []}
          selectedProductTypeIds={filters.product_type_ids || []}
          onSelectProductId={onSelectProduct}
          onSelectProductTypeId={onSelectProductType}
          onClear={onClearProduct}
          schoolYear={schoolYear ?? undefined}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <FrequencySelectMultiple
          placeholder={labels[FilterKeys.Frequency]}
          onSelectType={onSelectFrequency}
          selectedTypes={filters.frequency_types || []}
          onClear={onClearFrequency}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <FeeStatusSelectMultiple
          placeholder={labels[FilterKeys.FeeStatus]}
          onSelectStatus={onSelectFeeStatus}
          selectedStatuses={filters.fee_status || []}
          onClear={onClearFeeStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <DayPastDueSelectMultiple
          placeholder={labels[FilterKeys.DayPastDue]}
          onSelectType={onSelectDayPastDue}
          selectedTypes={filters.day_past_due || []}
          onClear={onClearDayPastDue}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <AgeGroupSelectMultiple
          allowArchived
          userRole={SchoolUserRole.Student}
          placeholder={labels[FilterKeys.InvoicesAgeGroup]}
          schoolId={schoolId}
          onSelect={onSelectAgeGroup}
          selectedIds={filters.age_group_ids || []}
          onClear={onClearAgeGroup}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ArrangeBySelect
          options={PAYABLE_FEES_ARRANGE_BY_FILTER_KEYS}
          selectedOption={arrangeBy}
          placeholder={labels.arrangeBy}
          onSelectOption={setArrangeBy}
          onOpen={preventClose}
          onClose={allowClose}
        />
      </FiltersSidebar>
    );
  },
);
